import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Backdrop, ListSubheader, MenuItem, Portal, Select, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppLoader from "../AppLoader/AppLoader";
import { DISCO_COMPANIES } from "../../constants/discoCompanies";
import { setSelectedAdminCompanyCode } from "../../features/authentication/authenticationSlice";
import { updateOC } from "../../features/officeCodeForName/officeCodeForName";
import { AllSubDivisions } from "../../network/APIs/APIs";
import { companyName, getLogo } from "../../services/Functions";
import { useCompanySelectOptions } from "../../services/companySelectOptions";
import {
  ENABLED_DISCO_COMPANY_CODES,
  hasHeaderDiscoSwitcher,
  isEnabledDiscoCompany,
  isSelectableCompanyDropdownOption,
  persistAdminCompanySelectionToStorage,
} from "../../utils/adminCompany";
import { usePageRemount } from "../../utils/pageRemountContext";
import "./HeaderDiscoSwitcher.scss";

/** All DISCOs from API; enabled first (MEPCO/FESCO/IESCO), then rest by code. */
const buildDiscoOptions = (apiNames = []) => {
  const orderMap = Object.fromEntries(
    ENABLED_DISCO_COMPANY_CODES.map((code, index) => [code, index]),
  );
  const names =
    apiNames.length > 0
      ? apiNames
      : DISCO_COMPANIES.map((company) => ({
          value: company.code,
          label: company.name,
        }));

  return [...names]
    .map((option) => {
      const code = String(option.value);
      const fullName = companyName(code);
      return {
        code,
        shortName:
          option.label ||
          DISCO_COMPANIES.find((company) => company.code === code)?.name ||
          code,
        fullName: fullName !== "DISTRIBUTION COMPANY NAME" ? fullName : "",
        logo: getLogo(code),
        enabled: isEnabledDiscoCompany(code),
      };
    })
    .sort((left, right) => {
      const leftOrder = orderMap[left.code];
      const rightOrder = orderMap[right.code];
      if (leftOrder != null && rightOrder != null) return leftOrder - rightOrder;
      if (leftOrder != null) return -1;
      if (rightOrder != null) return 1;
      return Number(left.code) - Number(right.code);
    });
};

const renderLogo = (option) => (
  <span className="header-disco-switcher__logo-wrap">
    <img src={option.logo} alt="" className="header-disco-switcher__logo" />
  </span>
);

/**
 * Top-bar DISCO switcher — code 10 admins only (the HQ role that is fixed to PITC is excluded).
 * Every page shows the selected company's data, so the control says so on its face.
 */
const HeaderDiscoSwitcher = () => {
  const dispatch = useDispatch();
  const remountPage = usePageRemount();
  const { code, roleId, token, hierarchy, selectedAdminCompanyCode } = useSelector(
    (state) => state.auth,
  );
  const [switching, setSwitching] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Hover hint; never shown over the open menu
  const [hintOpen, setHintOpen] = useState(false);
  const isDiscoAdmin = hasHeaderDiscoSwitcher(code, roleId);
  const apiNames = useCompanySelectOptions({
    includePitc: false,
    token,
    autoFetch: isDiscoAdmin,
  });
  const discoOptions = useMemo(() => buildDiscoOptions(apiNames), [apiNames]);

  if (!isDiscoAdmin) return null;

  const currentCode = String(selectedAdminCompanyCode || "");
  const value = discoOptions.some((option) => option.code === currentCode)
    ? currentCode
    : "";
  const isSelectable = (option) =>
    isSelectableCompanyDropdownOption(option.code, currentCode);
  const availableOptions = discoOptions.filter(isSelectable);
  const unavailableOptions = discoOptions.filter((option) => !isSelectable(option));

  const handleChange = async (event) => {
    const nextCode = String(event.target.value || "");
    if (!nextCode || nextCode === currentCode) return;
    if (!isEnabledDiscoCompany(nextCode)) return;
    setSwitching(true);
    try {
      // Same steps as confirming a DISCO on the login picker; sub-divisions load first so
      // pages never see the new DISCO with the old DISCO's hierarchy lists
      await AllSubDivisions(token, hierarchy, nextCode);
      persistAdminCompanySelectionToStorage(nextCode);
      dispatch(setSelectedAdminCompanyCode(nextCode));
      dispatch(updateOC({ officeCode: nextCode }));
      // Stay on this page: re-open it so it loads fresh for the new DISCO
      remountPage();
    } finally {
      setSwitching(false);
    }
  };

  const renderItem = (option, disabled) => (
    <MenuItem
      key={option.code}
      value={option.code}
      disabled={disabled}
      className={`header-disco-switcher__item${
        disabled ? " header-disco-switcher__item--disabled" : ""
      }${option.code === value ? " header-disco-switcher__item--current" : ""}`}
    >
      {renderLogo(option)}
      <span className="header-disco-switcher__item-text">
        <span className="header-disco-switcher__item-name">{option.shortName}</span>
        {option.fullName && (
          <span className="header-disco-switcher__item-full">{option.fullName}</span>
        )}
      </span>
      {option.code === value ? (
        <CheckRoundedIcon className="header-disco-switcher__check" />
      ) : disabled ? (
        <span className="header-disco-switcher__soon">Soon</span>
      ) : null}
    </MenuItem>
  );

  return (
    <>
      <Tooltip
        title="Change company — every page shows the selected company's data"
        arrow
        open={hintOpen && !menuOpen}
        onOpen={() => setHintOpen(true)}
        onClose={() => setHintOpen(false)}
        disableHoverListener={menuOpen}
        // Focus returns here when the menu closes; that must not pop the hint back up
        disableFocusListener
      >
        <Select
          value={value}
          onChange={handleChange}
          open={menuOpen}
          onOpen={() => {
            setHintOpen(false);
            setMenuOpen(true);
          }}
          onClose={() => setMenuOpen(false)}
          displayEmpty
          disabled={switching}
          size="small"
          variant="outlined"
          className={`header-disco-switcher${menuOpen ? " header-disco-switcher--open" : ""}`}
          IconComponent={KeyboardArrowDownRoundedIcon}
          inputProps={{ "aria-label": "Company whose data is shown — change company" }}
          renderValue={(selected) => {
            const option = discoOptions.find((item) => item.code === selected);
            return (
              <span className="header-disco-switcher__trigger">
                {option ? renderLogo(option) : null}
                <span className="header-disco-switcher__trigger-text">
                  <span className="header-disco-switcher__caption">Viewing data of</span>
                  <span className="header-disco-switcher__name">
                    {option ? option.shortName : "Select company"}
                  </span>
                </span>
              </span>
            );
          }}
          MenuProps={{
            PaperProps: { className: "header-disco-switcher__menu" },
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            transformOrigin: { vertical: "top", horizontal: "right" },
          }}
        >
          <ListSubheader className="header-disco-switcher__menu-head" disableSticky>
            <span className="header-disco-switcher__menu-title">Switch company</span>
            <span className="header-disco-switcher__menu-hint">
              All pages show data for the company you pick.
            </span>
          </ListSubheader>
          <ListSubheader className="header-disco-switcher__group" disableSticky>
            Available
          </ListSubheader>
          {availableOptions.map((option) => renderItem(option, false))}
          {unavailableOptions.length > 0 && (
            <ListSubheader className="header-disco-switcher__group" disableSticky>
              Not enabled yet
            </ListSubheader>
          )}
          {unavailableOptions.map((option) => renderItem(option, true))}
        </Select>
      </Tooltip>

      {/* Portal to <body>: inside the top bar the overlay could not cover the sidebar */}
      <Portal>
        <Backdrop open={switching} sx={{ color: "#fff", zIndex: 9999 }}>
          <AppLoader />
        </Backdrop>
      </Portal>
    </>
  );
};

export default HeaderDiscoSwitcher;
